import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AgendaGradeLinha, AgendaPlanoPaciente } from '../../../../interfaces/agenda';
import { AgendaModaisService } from '../../../../services/agenda/agenda-modais.service';
import { AgendaService, mensagemErroAgenda } from '../../../../services/agenda/agenda.service';
import { corpoTrocarPaciente, mesmoPaciente } from '../agenda-acoes';
import { CONVENIO_CARTAO } from '../agenda-agendamento/agenda-agendamento';
import { formatarCpf } from '../agenda-grade/agenda-grade';
import { AgendaPacienteComponent } from '../agenda-paciente/agenda-paciente.component';
import { AgendaPacienteEscolhido } from '../agenda-paciente/agenda-paciente';

/** Troca o titular do agendamento; horário, classificação, convênio e canal ficam como estão. */
@Component({
  selector: 'app-agenda-trocar-paciente',
  standalone: true,
  imports: [CommonModule, AgendaPacienteComponent],
  templateUrl: './agenda-trocar-paciente.component.html',
  styleUrl: './agenda-trocar-paciente.component.scss',
})
export class AgendaTrocarPacienteComponent implements OnInit, OnDestroy {
  @Input({ required: true }) linha!: AgendaGradeLinha;

  @Output() fechar = new EventEmitter<void>();
  @Output() salvo = new EventEmitter<string>();

  readonly formatarCpf = formatarCpf;

  novo: AgendaPacienteEscolhido | null = null;
  /** undefined = ainda lendo o plano do novo paciente. */
  plano: AgendaPlanoPaciente | null | undefined = undefined;
  salvando = false;
  erro = '';

  private fecharRegistro: (() => void) | null = null;

  constructor(private api: AgendaService, private modais: AgendaModaisService) {}

  ngOnInit() {
    this.fecharRegistro = this.modais.abrir();
  }

  ngOnDestroy() {
    this.fecharRegistro?.();
    this.fecharRegistro = null;
  }

  @HostListener('document:keydown.escape')
  aoEsc() {
    if (!this.modais.coberto()) this.cancelar();
  }

  cancelar() {
    if (!this.salvando) this.fechar.emit();
  }

  get dataBr(): string {
    const [a, m, d] = (this.linha.dtAgenda ?? '').slice(0, 10).split('-');
    return d ? `${d}/${m}/${a}` : '';
  }

  get mesmo(): boolean { return mesmoPaciente(this.linha, this.novo?.cdPessoaFisica); }

  /**
   * A troca não mexe em convênio nem categoria: no cartão, o novo titular sem o mesmo plano fica
   * com a categoria do anterior até alguém ajustar em Alterar.
   */
  get aviso224(): string | null {
    if (this.linha.convenio?.cdConvenio !== CONVENIO_CARTAO || !this.novo || this.mesmo || this.plano === undefined) return null;
    const p = this.plano;
    const mesmaCategoria = !!p?.temPlano && p.cdConvenio === CONVENIO_CARTAO && String(p.cdCategoria) === String(this.linha.categoria?.cdCategoria ?? '');
    if (mesmaCategoria) return null;
    const cat = this.linha.categoria?.dsCategoria || this.linha.categoria?.cdCategoria || '';
    const doNovo = p?.temPlano ? `O novo paciente tem ${p.dsCategoria || 'outro plano'}` : 'O novo paciente não tem plano do cartão';
    return `O agendamento está no convênio ${CONVENIO_CARTAO}${cat ? ` (${cat})` : ''}. ${doNovo}; a troca não ajusta convênio nem categoria. Corrija depois em Alterar.`;
  }

  aoMudarPaciente(p: AgendaPacienteEscolhido | null) {
    this.novo = p;
    this.plano = undefined;
    this.erro = '';
  }

  aoCarregarPlano(p: AgendaPlanoPaciente | null) {
    this.plano = p;
  }

  async confirmar() {
    if (this.salvando || !this.novo || this.mesmo) return;
    this.salvando = true;
    this.erro = '';
    const novo = this.novo;
    try {
      await this.api.trocarPaciente(this.linha.nrSequencia, corpoTrocarPaciente(this.linha, novo.cdPessoaFisica));
      const antigo = this.linha.paciente?.nmPaciente || 'paciente anterior';
      this.salvo.emit(`Horário ${this.linha.hrInicio}: ${antigo} → ${novo.nome}.`);
    } catch (e) {
      this.erro = mensagemErroAgenda(e, 'Erro ao trocar o paciente do agendamento.');
    } finally {
      this.salvando = false;
    }
  }
}
